import { g, layers } from '@hcie/core';
import { EventBus, renderVectorShapesToCanvas } from '@hcie/shared';
import type { Shape } from '@hcie/shared';
import { createVectorOverlay } from './dom-overlay';
import type { VectorToolManagerAPI, VectorEditContext } from './types';
import { toolToShape } from './shape-adapter';
import { bindVectorToolStyleSync } from './style-sync';
import { findHitShapeIndex } from './hit-test';
import { handleDir, getCanvasWrapper, getScrollArea, getZoom, type HandleId } from './utils';

type EditTarget = { layerIndex: number; shapeIndex: number } | null;

export class VectorToolManager implements VectorToolManagerAPI {
    isEditing = false;
    currentShape: Shape | null = null;
    private editTarget: EditTarget = null;
    private overlay = createVectorOverlay({
        onCommit: () => this.commitEdit(),
        onCancel: () => this.cancelEdit(),
        onHandleStart: (handle, e) => this.onHandleStart(handle, e),
        onMoveStart: (e) => this.onMoveStart(e),
        onRotateStart: (e) => this.onRotateStart(e),
    });

    private dragMode: 'none' | 'move' | 'handle' | 'rotate' = 'none';
    private activeHandle: HandleId | null = null;
    private dragStartClient = { x: 0, y: 0 };
    private dragStartShape: Shape | null = null;
    private lastZoom = g.zoom || 1;
    private unbindStyleSync: null | (() => void) = null;
    private rotateStartClient = { x: 0, y: 0 };
    private rotateStartAngle = 0;

    constructor() {
        const self = this;
        const wrapper = getCanvasWrapper();
        if (wrapper) {
            wrapper.appendChild(this.overlay.root);
        } else {
            setTimeout(() => {
                const w = getCanvasWrapper();
                if (w && !w.contains(this.overlay.root)) w.appendChild(this.overlay.root);
            }, 0);
        }
        this.overlay.setVisible(false);

        window.addEventListener('zoomChanged', () => this.refreshOverlay());
        getScrollArea()?.addEventListener('scroll', () => this.refreshOverlay(), { passive: true });

        this.unbindStyleSync = bindVectorToolStyleSync({
            get isEditing() { return self.isEditing; },
            get currentShape() { return self.currentShape; },
            updateStatusBarInfo: (shape: Shape) => this.updateStatusBarInfo(shape),
            refreshOverlayForExternalStyleChange: () => this.refreshOverlay(),
        } as any);
    }

    teardown() {
        if (this.unbindStyleSync) this.unbindStyleSync();
        this.overlay.destroy();
        this.isEditing = false;
        this.currentShape = null;
        this.editTarget = null;
    }

    startNewFromTool(toolId: string, startX: number, startY: number, endX: number, endY: number, layerIndex: number) {
        const shape = toolToShape(toolId, startX, startY, endX, endY);
        if (!shape) return;
        this.startEdit(shape, { layerIndex, shapeIndex: -1 });
    }

    startEdit(shape: Shape, opts: { layerIndex: number; shapeIndex: number }) {
        this.isEditing = true;
        this.updateStatusBarInfo(JSON.parse(JSON.stringify(shape)));
        this.currentShape = JSON.parse(JSON.stringify(shape));
        this.editTarget = { layerIndex: opts.layerIndex, shapeIndex: opts.shapeIndex };
        (window as any).vectorEditingTarget = { layerIndex: opts.layerIndex, shapeIndex: opts.shapeIndex };
        this.overlay.setVisible(true);
        this.refreshOverlay();
        if (this.currentShape) {
            this.notifyVectorSelectionChanged(true, (this.currentShape as any).type, (this.currentShape as any).style);
            this.updateStatusBarInfo(this.currentShape);
        }
    }

    updateOverlay(ctx: VectorEditContext, shape: Shape) {
        this.lastZoom = getZoom(ctx);
        this.currentShape = shape;
        this.refreshOverlay();
    }

    updateStatusBarInfo(_shape: Shape) {
        EventBus.emit('DRAWING_STATUS_UPDATE', undefined);
    }

    updateShapeGeometry(_ctx: any, updates: Record<string, number>) {
        if (!this.isEditing || !this.currentShape) return;
        const s: any = this.currentShape as any;
        const has = (k: string) => Object.prototype.hasOwnProperty.call(updates, k);

        if (has('x1')) s.x1 = updates.x1;
        if (has('y1')) s.y1 = updates.y1;
        if (has('x2')) s.x2 = updates.x2;
        if (has('y2')) s.y2 = updates.y2;

        if (s.type === 'circle' && has('radius')) {
            const r = Math.max(1, updates.radius);
            const dx0 = (s.x2 - s.x1);
            const dy0 = (s.y2 - s.y1);
            const len = Math.hypot(dx0, dy0) || 1;
            const ux = dx0 / len;
            const uy = dy0 / len;
            s.x2 = s.x1 + ux * r;
            s.y2 = s.y1 + uy * r;
        }

        if (has('angle')) {
            const ang = Number.isFinite(updates.angle) ? updates.angle : 0;
            if (s.type === 'line') {
                const dist = Math.hypot(s.x2 - s.x1, s.y2 - s.y1) || 1;
                const rad = ang * Math.PI / 180;
                s.x2 = s.x1 + dist * Math.cos(rad);
                s.y2 = s.y1 + dist * Math.sin(rad);
            } else {
                if (!s.style) s.style = {};
                s.style.angle = ((ang % 360) + 360) % 360;
            }
        }

        this.emitRealtimeSync();
    }

    commitEdit() {
        if (!this.isEditing || !this.currentShape || !this.editTarget) return;
        const { layerIndex, shapeIndex } = this.editTarget;
        const layer: any = (layers as any)[layerIndex];
        if (!layer) return;

        if (layer.type === 'vector') {
            if (!Array.isArray(layer.shapes)) layer.shapes = [];
            
            // [HISTORY_RECORDING_START]
            const prevShapes = JSON.parse(JSON.stringify(layer.shapes));
            const isNewShape = shapeIndex < 0;
            const desc = isNewShape ? `Add Vector ${this.currentShape.type}` : `Edit Vector ${this.currentShape.type}`;

            if (!isNewShape && layer.shapes[shapeIndex]) {
                layer.shapes[shapeIndex] = JSON.parse(JSON.stringify(this.currentShape));
            } else {
                layer.shapes.push(JSON.parse(JSON.stringify(this.currentShape)));
            }
            
            const nextShapes = JSON.parse(JSON.stringify(layer.shapes));
            const hm = (window as any).historyManager;
            const makeVecAction = (window as any).makeNamedVectorAction;
            
            if (hm && makeVecAction) {
                hm.push(makeVecAction(layerIndex, prevShapes, nextShapes, desc));
            }
            // [HISTORY_RECORDING_END]

            this.finish();
            EventBus.emit('RENDER_LAYERS', undefined);
            EventBus.emit('UPDATE_LAYER_PANEL', undefined);
            return;
        }

        // Raster ghost mode: apply the vector shape to raster ctx on commit.
        const rasterGhost = (window as any).vectorRasterGhost;
        if (layer.type !== 'vector' && rasterGhost && rasterGhost.layerIndex === layerIndex && layer.ctx) {
            try {
                const prev = rasterGhost.prev;
                const desc = rasterGhost.desc || 'Vector Shape';
                
                // Use imported renderVectorShapesToCanvas for stateless rasterization
                renderVectorShapesToCanvas(layer.ctx as CanvasRenderingContext2D, [this.currentShape!]);
                
                const next = layer.ctx.getImageData(0, 0, g.image_width, g.image_height);
                const hm = (window as any).historyManager;
                const makeNamed = (window as any).makeNamedDrawAction;
                if (hm && typeof makeNamed === 'function' && prev) {
                    hm.push(makeNamed(layerIndex, prev, next, desc));
                }
            } finally {
                (window as any).vectorRasterGhost = null;
            }
        }

        this.finish();
        EventBus.emit('RENDER_LAYERS', undefined);
    }

    cancelEdit() {
        if (!this.isEditing) return;
        (window as any).vectorRasterGhost = null;
        this.finish();
        EventBus.emit('RENDER_LAYERS', undefined);
    }

    trySelectShapeAt(x: number, y: number, layerIndex: number) {
        if (this.isEditing) return true;
        const layer: any = (layers as any)[layerIndex];
        if (!layer || layer.type !== 'vector' || !Array.isArray(layer.shapes)) {
            this.notifyVectorSelectionChanged(false);
            return false;
        }
        const hit = findHitShapeIndex(layer.shapes as Shape[], x, y);
        if (hit < 0) {
            this.notifyVectorSelectionChanged(false);
            return false;
        }
        const shape = layer.shapes[hit] as Shape;
        this.startEdit(shape, { layerIndex, shapeIndex: hit });
        EventBus.emit('RENDER_LAYERS', undefined);
        return true;
    }

    private finish() {
        this.isEditing = false;
        this.currentShape = null;
        this.editTarget = null;
        (window as any).vectorEditingTarget = null;
        this.dragMode = 'none';
        this.activeHandle = null;
        this.dragStartShape = null;
        this.overlay.setVisible(false);
        this.notifyVectorSelectionChanged(false);
    }

    private refreshOverlay() {
        if (!this.isEditing || !this.currentShape) return;
        const zoom = g.zoom || this.lastZoom || 1;
        this.overlay.update(this.currentShape, zoom);
    }

    private onMoveStart(e: MouseEvent) {
        if (!this.currentShape) return;
        e.preventDefault();
        e.stopPropagation();
        this.dragMode = 'move';
        this.dragStartClient = { x: e.clientX, y: e.clientY };
        this.dragStartShape = JSON.parse(JSON.stringify(this.currentShape));
        document.addEventListener('mousemove', this.onDocMove, true);
        document.addEventListener('mouseup', this.onDocUp, true);
    }

    private onHandleStart(handle: HandleId, e: MouseEvent) {
        if (!this.currentShape) return;
        e.preventDefault();
        e.stopPropagation();
        this.dragMode = 'handle';
        this.activeHandle = handle;
        this.dragStartClient = { x: e.clientX, y: e.clientY };
        this.dragStartShape = JSON.parse(JSON.stringify(this.currentShape));
        document.addEventListener('mousemove', this.onDocMove, true);
        document.addEventListener('mouseup', this.onDocUp, true);
    }

    private onRotateStart(e: MouseEvent) {
        if (!this.currentShape) return;
        e.preventDefault();
        e.stopPropagation();
        this.dragMode = 'rotate';
        this.rotateStartClient = { x: e.clientX, y: e.clientY };
        this.dragStartShape = JSON.parse(JSON.stringify(this.currentShape));
        this.rotateStartAngle = ((this.dragStartShape as any)?.style?.angle || 0) as number;
        document.addEventListener('mousemove', this.onDocMove, true);
        document.addEventListener('mouseup', this.onDocUp, true);
    }

    private onDocMove = (e: MouseEvent) => {
        if (!this.dragStartShape || !this.currentShape) return;
        const zoom = g.zoom || 1;
        const dx = (e.clientX - this.dragStartClient.x) / zoom;
        const dy = (e.clientY - this.dragStartClient.y) / zoom;

        if (this.dragMode === 'move') {
            this.currentShape.x1 = this.dragStartShape.x1 + dx;
            this.currentShape.y1 = this.dragStartShape.y1 + dy;
            this.currentShape.x2 = this.dragStartShape.x2 + dx;
            this.currentShape.y2 = this.dragStartShape.y2 + dy;
        } else if (this.dragMode === 'rotate') {
            const s: any = this.dragStartShape as any;
            const can = document.getElementById('drawingCanvas') as HTMLCanvasElement | null;
            const rect = can?.getBoundingClientRect();
            if (rect) {
                let cx = (Math.min(s.x1, s.x2) + Math.max(s.x1, s.x2)) / 2;
                let cy = (Math.min(s.y1, s.y2) + Math.max(s.y1, s.y2)) / 2;
                if (s.type === 'circle') { cx = s.x1; cy = s.y1; }
                const screenCx = rect.left + cx * zoom;
                const screenCy = rect.top + cy * zoom;
                const a0 = Math.atan2(this.rotateStartClient.y - screenCy, this.rotateStartClient.x - screenCx) * 180 / Math.PI;
                const a1 = Math.atan2(e.clientY - screenCy, e.clientX - screenCx) * 180 / Math.PI;
                const diff = a1 - a0;

                if ((this.currentShape as any).type === 'line') {
                    const mx = (s.x1 + s.x2) / 2;
                    const my = (s.y1 + s.y2) / 2;
                    let rad = diff * Math.PI / 180;
                    if (e.shiftKey) {
                         const angle = diff;
                         const snappedAngle = Math.round(angle / 15) * 15;
                         rad = snappedAngle * Math.PI / 180;
                    }
                    const rot = (px: number, py: number) => {
                        const x = px - mx, y = py - my;
                        return { x: mx + x * Math.cos(rad) - y * Math.sin(rad), y: my + x * Math.sin(rad) + y * Math.cos(rad) };
                    };
                    const p1 = rot(s.x1, s.y1);
                    const p2 = rot(s.x2, s.y2);
                    (this.currentShape as any).x1 = p1.x; (this.currentShape as any).y1 = p1.y;
                    (this.currentShape as any).x2 = p2.x; (this.currentShape as any).y2 = p2.y;
                } else {
                    if (!(this.currentShape as any).style) (this.currentShape as any).style = {};
                    let angle = ((this.rotateStartAngle + diff) % 360 + 360) % 360;
                    if (e.shiftKey) {
                        angle = Math.round(angle / 15) * 15;
                    }
                    (this.currentShape as any).style.angle = angle;
                }
            }
            this.emitRealtimeSync();
        } else if (this.dragMode === 'handle' && this.activeHandle) {
            const s = JSON.parse(JSON.stringify(this.dragStartShape)) as Shape;
            if ((s as any).type === 'circle') {
                const cx = (s as any).x1;
                const cy = (s as any).y1;
                const r0 = Math.hypot((s as any).x2 - (s as any).x1, (s as any).y2 - (s as any).y1);
                const dir = handleDir(this.activeHandle);
                const len = Math.hypot(dir.x, dir.y) || 1;
                const ux = dir.x / len;
                const uy = dir.y / len;
                const hx0 = cx + ux * r0;
                const hy0 = cy + uy * r0;
                const hx1 = hx0 + dx;
                const hy1 = hy0 + dy;
                const newR = Math.max(1, Math.hypot(hx1 - cx, hy1 - cy));
                (this.currentShape as any).x1 = cx;
                (this.currentShape as any).y1 = cy;
                (this.currentShape as any).x2 = cx + ux * newR;
                (this.currentShape as any).y2 = cy + uy * newR;
            } else {
                const minX = Math.min((s as any).x1, (s as any).x2);
                const maxX = Math.max((s as any).x1, (s as any).x2);
                const minY = Math.min((s as any).y1, (s as any).y2);
                const maxY = Math.max((s as any).y1, (s as any).y2);
                let x1 = minX, x2 = maxX, y1 = minY, y2 = maxY;
                if (this.activeHandle.includes('w')) x1 = minX + dx;
                if (this.activeHandle.includes('e')) x2 = maxX + dx;
                if (this.activeHandle.includes('n')) y1 = minY + dy;
                if (this.activeHandle.includes('s')) y2 = maxY + dy;

                if (e.shiftKey) {
                    const w = x2 - x1;
                    const h = y2 - y1;
                    const side = Math.max(Math.abs(w), Math.abs(h));
                    if (this.activeHandle.includes('w')) x1 = x2 - (w < 0 ? -side : side);
                    else x2 = x1 + (w < 0 ? -side : side);
                    if (this.activeHandle.includes('n')) y1 = y2 - (h < 0 ? -side : side);
                    else y2 = y1 + (h < 0 ? -side : side);
                }

                (this.currentShape as any).x1 = x1;
                (this.currentShape as any).y1 = y1;
                (this.currentShape as any).x2 = x2;
                (this.currentShape as any).y2 = y2;
            }
        }

        this.emitRealtimeSync();
    };

    private onDocUp = (_e: MouseEvent) => {
        document.removeEventListener('mousemove', this.onDocMove, true);
        document.removeEventListener('mouseup', this.onDocUp, true);
        this.dragMode = 'none';
        this.activeHandle = null;
        this.dragStartShape = null;
    };

    private emitRealtimeSync() {
        if (!this.currentShape) return;
        this.refreshOverlay();
        this.notifyVectorSelectionChanged(true, (this.currentShape as any).type, (this.currentShape as any).style);
        window.dispatchEvent(new CustomEvent('vectorShapeGeometryChanged', {
            detail: {
                type: (this.currentShape as any).type,
                x1: (this.currentShape as any).x1,
                y1: (this.currentShape as any).y1,
                x2: (this.currentShape as any).x2,
                y2: (this.currentShape as any).y2,
                angle: (this.currentShape as any).type === 'line'
                    ? (Math.atan2((this.currentShape as any).y2 - (this.currentShape as any).y1, (this.currentShape as any).x2 - (this.currentShape as any).x1) * 180 / Math.PI)
                    : ((this.currentShape as any).style?.angle || 0),
            }
        }));
        this.updateStatusBarInfo(this.currentShape);
        EventBus.emit('RENDER_LAYERS', undefined);
    }

    private notifyVectorSelectionChanged(hasSelection: boolean, shapeType: any = null, style: any = null) {
        const s: any = this.currentShape as any;
        if (!hasSelection || !shapeType || !style || !s) {
            (window as any).vectorSelection = { hasSelection: false, shapeType: null, style: null };
            window.dispatchEvent(new CustomEvent('vectorShapeSelectionChanged'));
            return;
        }
        (window as any).vectorSelection = {
            hasSelection: true,
            shapeType,
            type: shapeType,
            style,
            x1: s.x1, y1: s.y1, x2: s.x2, y2: s.y2,
        };
        window.dispatchEvent(new CustomEvent('vectorShapeSelectionChanged'));
    }
}

